import React, { useCallback } from 'react';

import { LiveCardProps, LiveStatusEnum, useLives } from 'features/lives';
import ConditionalLink from 'components/conditionalLink';
import styled from 'styled-components';
import { Flex, Typography } from 'components';
import { Icon } from 'components/icon';
import { createMarkup } from 'app/utils';
import formatDateFromUtcToTimezone from 'app/utils/formatDateFromUtcToTimezone';

const Siding = styled(Flex)`
  background: ${({ theme }) => theme.colors.black80};
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 4px;
  z-index: 20;
`;

const BackgroundOpacity = styled.div`
  border-radius: 4px;
  background: linear-gradient(0deg, ${({ theme }) => theme.colors.primary_400}, ${({ theme }) => theme.colors.primary_400}),
    linear-gradient(90deg, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0.1) 100%), url(melver-raony.png);
  background-blend-mode: multiply, normal, normal;
  opacity: 0.5;
  display: none;
  position: absolute;
`;

const Content = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  justify-content: space-between;
  z-index: 10;
`;

const Head = styled.div`
  display: flex;
  justify-content: space-between;
`;

const DateWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1rem;
`;

const Day = styled.div`
  font-size: 3.75rem;
  font-weight: 100;
  line-height: 1;
`;

const Month = styled.div`
  font-size: 0.875rem;
  line-height: 1;
  text-transform: uppercase;
  font-weight: 400;
  margin-top: 0.25rem;
`;

const TimeWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 1rem;
`;

const Time = styled.div`
  font-size: 1rem;
  line-height: 1;
  font-weight: 700;
  padding: 6px 8px;
  border-radius: 4px;
  text-transform: uppercase;
`;

const Foot = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1rem;
`;

const Hat = styled.div`
  font-size: 0.75rem;
  line-height: 0.875rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
`;

const Name = styled.div`
  font-size: 1.125rem;
  line-height: 1.313rem;
  font-weight: 700;
`;

const TeacherName = styled.div`
  font-size: 1rem;
  line-height: 1.125rem;
  font-weight: 500;
  margin-top: 0.5rem;
`;

const LiveButton = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  position: relative;
  z-index: 10;

  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
`;

const LiveButtonLabel = styled.div`
  font-size: 1rem;
  line-height: 1.125em;
  font-weight: 700;
  text-transform: uppercase;
`;

const LiveButtonIcon = styled(Icon)`
  width: 24px;
`;

const LiveFloatButton = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.85rem 1rem;
  position: relative;
  z-index: 10;
  border-radius: 4px;
`;

const LiveFloatButtonLabel = styled.div`
  font-size: 1rem;
  line-height: 1.125em;
  font-weight: 700;
  text-transform: uppercase;
`;

const LiveFloatButtonIcon = styled(Icon)`
  width: 24px;
`;

const Wrapper = styled(ConditionalLink)`
  display: flex;
  flex-direction: column;

  width: ${(props: any) => (props['data-size'] === 'default' ? '280px' : props['data-size'] === 'small' ? '240px' : '320px')};
  height: ${(props: any) => (props['data-size'] === 'large' ? '395px' : '340px')};

  position: relative;
  z-index: 5;

  background-color: ${(props: any) => (props['data-image'] ? props.theme.colors.gray_100 : 'white')};

  cursor: ${(props: any) => (props['data-disabled'] ? 'inherit' : 'pointer')};

  ${(props: any) => {
    if (props['data-image']) {
      return `
        background: linear-gradient(180deg, rgba(30, 29, 31, 0.4) 12.65%, rgba(30, 29, 31, 0.1) 32.6%, rgba(30, 29, 31, 0.9) 64.41%), url(${props['data-image']});
        background-size: cover;
        background-position: top center;
      `;
    }

    return `
      border: 1px solid ${props.theme.colors.gray_100};
    `;
  }}

  ${(props: any) => {
    if (props['data-confirmation']) {
      return `
        border-radius: 4px;

        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
        
        ${LiveButton} {
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
        }

        ${Siding} {
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
        }

        ${BackgroundOpacity} {
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
        }
      `;
    }

    if (props['data-confirmationpage']) {
      return `
        border-radius: 4px;

        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
        
        ${LiveButton} {
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
        }

        ${Siding} {
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
        }

        ${BackgroundOpacity} {
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
        }
      `;
    }

    return `
      border-radius: 4px;
    `;
  }};

  ${Day},
  ${Month} {
    color: ${(props: any) => (props['data-image'] ? props.theme.colors.white : props.theme.colors.black50)};
  }

  ${Time} {
    background: ${({ theme }) => theme.colors.primary_600};
    color: ${({ theme }) => theme.colors.primary_contrast};

    &.live {
      background: ${({ theme }) => theme.colors.alert_400};
      color: ${({ theme }) => theme.colors.white};
    }
  }

  ${Hat} {
    color: ${({ theme }) => theme.colors.primary_400};
  }

  ${Name} {
    color: ${(props: any) => (props['data-image'] ? props.theme.colors.white : props.theme.colors.dark_350)};
  }

  ${TeacherName} {
    color: ${(props: any) => (props['data-image'] ? props.theme.colors.white50 : props.theme.colors.black30)};
  }

  ${BackgroundOpacity} {
    width: 100%;
    height: ${(props: any) => (props['data-size'] === 'large' ? '395px' : '340px')};
  }

  ${LiveButton} {
    background: ${(props: any) => (props['data-image'] ? props.theme.colors.black30 : props.theme.colors.gray_100)};
  }

  ${LiveButtonLabel} {
    color: ${({ theme }) => theme.colors.primary_400};

    &.notEnrolled {
      color: ${({ theme }) => theme.colors.gray_500};
    }
  }

  ${LiveButtonIcon} {
    color: ${({ theme }) => theme.colors.primary_400};
    &.notEnrolled {
      color: ${({ theme }) => theme.colors.gray_500};
    }
  }

  ${LiveFloatButton} {
    background: ${(props: any) =>
      props['data-disabled']
        ? props['data-image']
          ? props.theme.colors.black30
          : props.theme.colors.black50
        : props['data-image']
        ? props.theme.colors.primary_400
        : props.theme.colors.black50};
  }

  ${LiveFloatButtonLabel} {
    color: ${({ theme }) => theme.colors.primary_contrast};
  }

  ${LiveFloatButtonIcon} {
    color: ${({ theme }) => theme.colors.primary_contrast};
  }

  ${(props: any) => {
    if (!props['data-disabled']) {
      return `
        &:hover {
          box-shadow: 0px 4px 8px ${props.theme.colors.black20};
          border-color: ${props.theme.colors.primary_400};

          ${BackgroundOpacity} {
            display: block;
          }

          ${LiveButton} {
            background: ${props.theme.colors.primary_400};
          }

          ${LiveButtonLabel} {
            color: ${props.theme.colors.primary_contrast};
          }

          ${LiveButtonIcon} {
            color: ${props.theme.colors.primary_contrast};
          }

          ${LiveFloatButton} {
            background: ${props.theme.colors.primary_200};
          }
        }
      `;
    }
  }}
`;

const Live = (liveCard: LiveCardProps) => {
  const {
    // uuid,
    hat,
    image,
    name,
    teacherName,
    buttonLabel,
    buttonIcon,
    link,
    linkType,
    linkTarget,
    date,
    showDate,
    liveStatus,
    disabled,
    size = 'default',
    hasConfirmation,
    isConfirmation,
    isConfirmationPage,
    hideButton = false,
    floatButton = false,
    onLiveClick,
    productEnrolled,
    // theme,
  } = liveCard;

  const { setOpenLiveConfirmation } = useLives();

  let formatedDate = date ? formatDateFromUtcToTimezone(date) : null;

  const handleClick = useCallback(() => {
    if (onLiveClick) onLiveClick();
    if (!isConfirmationPage && hasConfirmation && !disabled) setOpenLiveConfirmation(true, liveCard);
  }, [disabled, hasConfirmation, isConfirmationPage]);

  let finalButtonIcon = buttonIcon;
  if (isConfirmation || isConfirmationPage) {
    finalButtonIcon = 'openInNew';
  }

  if (!productEnrolled)
    return (
      <Wrapper
        type={'nolink'}
        to={''}
        target={linkTarget}
        component={Flex}
        onClick={() => {}}
        data-size={size}
        data-image={image}
        data-disabled={true}
        data-confirmation={isConfirmation}
        data-confirmationpage={isConfirmationPage}
      >
        <Content>
          <Head />

          <Foot>
            {!!hat && <Hat>{hat}</Hat>}

            <Name>{name}</Name>

            {!!teacherName && <TeacherName>com {teacherName}</TeacherName>}
          </Foot>
        </Content>

        <LiveButton>
          <LiveButtonLabel className="notEnrolled">{'Somente para alunos'}</LiveButtonLabel>
          <LiveButtonIcon className="notEnrolled" icon="lockOutline" />
        </LiveButton>
      </Wrapper>
    );

  return (
    <Wrapper
      type={!disabled && hasConfirmation ? 'nolink' : linkType}
      to={link || ''}
      target={linkTarget}
      component={Flex}
      onClick={handleClick}
      data-size={size}
      data-image={image}
      data-disabled={disabled}
      data-confirmation={isConfirmation}
      data-confirmationpage={isConfirmationPage}
    >
      {liveStatus === LiveStatusEnum.RecordedNotAvailable && (
        <Siding alignItems="center" justifyContent="center" p={6} textAlign="center">
          <div>
            <Typography type="type3" color="white" lineHeight="1.3">
              <div dangerouslySetInnerHTML={createMarkup(`A gravação da live <strong>${name}</strong> será liberada em breve.`)} />
            </Typography>

            {!isConfirmationPage && hasConfirmation && (
              <Typography
                onClick={() => setOpenLiveConfirmation(true, liveCard)}
                type="type1"
                color="primary_400"
                colorHover="primary_200"
                mt={6}
                upper
                fontWeight="bold"
                style={{ cursor: 'pointer' }}
              >
                Confirmar presença
              </Typography>
            )}
          </div>
        </Siding>
      )}

      <Content>
        <Head>
          <DateWrapper>
            {!!formatedDate && showDate && (
              <>
                <Day>{formatedDate.format('DD')}</Day>
                <Month>{formatedDate.format('MMMM')}</Month>
              </>
            )}
          </DateWrapper>

          <TimeWrapper>
            {!!formatedDate && showDate && productEnrolled && (
              <Time className={liveStatus === LiveStatusEnum.InProgress ? 'live' : ''}>
                {liveStatus === LiveStatusEnum.InProgress ? 'Ao Vivo' : formatedDate.format('HH:mm')}
              </Time>
            )}
          </TimeWrapper>
        </Head>

        <Foot>
          {!!hat && <Hat>{hat}</Hat>}

          <Name>{name}</Name>

          {!!teacherName && <TeacherName>com {teacherName}</TeacherName>}
        </Foot>
      </Content>

      {!hideButton &&
        (floatButton ? (
          <Flex flexGrow={0} flexDirection="column" p={3}>
            <LiveFloatButton>
              <LiveFloatButtonLabel>{buttonLabel}</LiveFloatButtonLabel>
              <LiveFloatButtonIcon icon={finalButtonIcon} />
            </LiveFloatButton>
          </Flex>
        ) : (
          <LiveButton>
            <LiveButtonLabel>{productEnrolled ? buttonLabel : ''} </LiveButtonLabel>
            <LiveButtonIcon icon={productEnrolled ? finalButtonIcon : 'lockOutline'} />
          </LiveButton>
        ))}

      {!floatButton && !!image && <BackgroundOpacity />}
    </Wrapper>
  );
};

export default Live;
